import { useRef, useCallback } from 'react';
import { DirectionType } from '../types';

interface SwipePosition {
  x: number;
  y: number;
}

export default function useSwipe(onSwipe: (direction: DirectionType) => void) {
  const touchStartRef = useRef<SwipePosition | null>(null);
  const minSwipeDistance = 50; // Minimum distance required for a swipe
  
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Store the initial touch position
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);
  
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    // If we don't have a start position, exit early
    if (!touchStartRef.current) return;
    
    // Get end touch position
    const touch = e.changedTouches[0];
    const endX = touch.clientX;
    const endY = touch.clientY;
    
    // Calculate deltas (how much the finger moved)
    const deltaX = endX - touchStartRef.current.x;
    const deltaY = endY - touchStartRef.current.y;
    
    // Check if swipe was long enough
    if (Math.abs(deltaX) < minSwipeDistance && Math.abs(deltaY) < minSwipeDistance) {
      return; // Too short to be a swipe
    }
    
    // Determine the direction of the swipe
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Horizontal swipe
      if (deltaX > 0) {
        onSwipe('RIGHT');
      } else {
        onSwipe('LEFT');
      }
    } else {
      // Vertical swipe
      if (deltaY > 0) {
        onSwipe('DOWN');
      } else {
        onSwipe('UP');
      }
    }
    
    // Reset touch start
    touchStartRef.current = null;
  }, [onSwipe]);
  
  // Prevent the default behavior to avoid scrolling during swipe
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (touchStartRef.current) {
      e.preventDefault();
    }
  }, []);
  
  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  };
}